import React from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '../ui/SEOHead';
import PageHero from '../ui/PageHero';
import SidebarInfo from '../ui/SidebarInfo';
import useScrollReveal from '../../hooks/useScrollReveal';
import {
  LANDING_PAGES_DATA,
  SERVICES_DATA,
  DOCTORS_DATA,
  PATIENT_CENTER_DATA,
  NAV_ITEMS,
} from '../../data/navigation';
import s from './PageContent.module.css';

// ─── Child card grids rendered below the rich content ─────────────────────────
function ServicesGrid() {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:'20px', marginTop:'40px' }}>
      {SERVICES_DATA.map((svc, i) => (
        <Link key={svc.slug} to={`/services/${svc.slug}`} style={{ textDecoration:'none' }}>
          <div style={{ borderRadius:'var(--radius-lg)', background:'var(--slate-50)', border:'1px solid var(--slate-100)', padding:'28px 24px', height:'100%', transition:'all var(--transition)' }}
            onMouseEnter={e=>e.currentTarget.style.boxShadow='var(--shadow-lift)'}
            onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
            <div style={{ fontSize:'28px', marginBottom:'12px' }}>{svc.icon}</div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:'17px', fontWeight:'500', color:'var(--blue-900)', marginBottom:'8px' }}>{svc.title}</div>
            <p style={{ fontSize:'13px', color:'var(--slate-400)', lineHeight:'1.7', marginBottom:'14px' }}>{svc.excerpt}</p>
            <span style={{ color:'var(--teal-500)', fontWeight:'600', fontSize:'13px' }}>Learn more →</span>
          </div>
        </Link>
      ))}
    </div>
  );
}

function DoctorsGrid() {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:'24px', marginTop:'40px' }}>
      {DOCTORS_DATA.map((doc) => (
        <Link key={doc.slug} to={`/doctors/${doc.slug}`} style={{ textDecoration:'none' }}>
          <div style={{ borderRadius:'var(--radius-xl)', overflow:'hidden', boxShadow:'var(--shadow-card)', background:'var(--white)', transition:'all var(--transition)' }}
            onMouseEnter={e=>e.currentTarget.style.boxShadow='var(--shadow-lift)'}
            onMouseLeave={e=>e.currentTarget.style.boxShadow='var(--shadow-card)'}>
            <div style={{ height:'220px', overflow:'hidden' }}>
              <img src={doc.image} alt={doc.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
            </div>
            <div style={{ padding:'20px' }}>
              <div style={{ fontSize:'10px', fontWeight:'600', letterSpacing:'.12em', textTransform:'uppercase', color:'var(--teal-500)', marginBottom:'4px' }}>{doc.badge}</div>
              <div style={{ fontFamily:'var(--font-display)', fontSize:'17px', color:'var(--blue-900)', marginBottom:'2px' }}>{doc.name}</div>
              <p style={{ fontSize:'12px', color:'var(--slate-400)', marginBottom:'12px' }}>{doc.specialty}</p>
              <span style={{ color:'var(--teal-500)', fontWeight:'600', fontSize:'12px' }}>View Profile →</span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

function PatientCenterGrid() {
  const visibleItems = NAV_ITEMS.find(n => n.path === '/patient-center')?.children || [];
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:'16px', marginTop:'40px' }}>
      {visibleItems.map((item) => {
        const data = PATIENT_CENTER_DATA[item.path.split('/').pop()];
        return (
          <Link key={item.path} to={item.path} style={{ textDecoration:'none' }}>
            <div style={{ borderRadius:'var(--radius-lg)', background:'var(--slate-50)', border:'1px solid var(--slate-100)', padding:'24px 20px', textAlign:'center', height:'100%', transition:'all var(--transition)' }}
              onMouseEnter={e=>e.currentTarget.style.boxShadow='var(--shadow-lift)'}
              onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
              <div style={{ fontSize:'28px', marginBottom:'10px' }}>{item.icon}</div>
              <div style={{ fontFamily:'var(--font-display)', fontSize:'15px', color:'var(--blue-900)', marginBottom:'6px' }}>{item.label}</div>
              <span style={{ color:'var(--teal-500)', fontWeight:'600', fontSize:'12px' }}>View →</span>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function ContactGrid() {
  const items = NAV_ITEMS.find(n => n.path === '/contact')?.children || [];
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:'16px', marginTop:'40px' }}>
      {items.map((item) => (
        <Link key={item.path} to={item.path} style={{ textDecoration:'none' }}>
          <div style={{ borderRadius:'var(--radius-lg)', background:'var(--slate-50)', border:'1px solid var(--slate-100)', padding:'24px 20px', textAlign:'center', height:'100%', transition:'all var(--transition)' }}
            onMouseEnter={e=>e.currentTarget.style.boxShadow='var(--shadow-lift)'}
            onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
            <div style={{ fontSize:'28px', marginBottom:'10px' }}>{item.icon}</div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:'15px', color:'var(--blue-900)', marginBottom:'6px' }}>{item.label}</div>
            <span style={{ color:'var(--teal-500)', fontWeight:'600', fontSize:'12px' }}>Go →</span>
          </div>
        </Link>
      ))}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function LandingPage({ pageKey: propKey }) {
  useScrollReveal();

  // pageKey can come from props (when used directly) or from the URL
  const params = useParams();
  const pageKey = propKey || params.pageKey || 'services';
  const page = LANDING_PAGES_DATA?.[pageKey];

  if (!page) return (
    <section className="section"><div className="container">
      <h2>Page not found</h2>
      <Link to="/" className="btn btn-primary" style={{ marginTop:'20px' }}>Go Home</Link>
    </div></section>
  );

  const breadcrumbLabel = page.title;

  return (
    <>
      <SEOHead
        title={page.seo?.title || page.title}
        description={page.seo?.description || page.excerpt}
        keywords={page.seo?.keywords}
        canonical={`/${pageKey}`}
      />
      <PageHero
        eyebrow={page.eyebrow}
        title={page.title}
        subtitle={page.excerpt}
        breadcrumbs={[{ label: breadcrumbLabel }]}
        image={page.image}
      />

      <section className="section">
        <div className="container">
          <div className={s.pageLayout}>

            {/* ── Main content ─────────────────────────────── */}
            <div>
              {/*
               * EDITABLE CONTENT AREA
               * ─────────────────────
               * Edit the `content` field in LANDING_PAGES_DATA (navigation.js).
               * Supports: <p> <ul> <ol> <h2> <h3> <img> <blockquote> etc.
               */}
              {page.content && (
                <div
                  className={s.richContent}
                  dangerouslySetInnerHTML={{ __html: page.content }}
                />
              )}

              {/* Auto-rendered child card grid */}
              {pageKey === 'services'        && <ServicesGrid />}
              {pageKey === 'doctors'         && <DoctorsGrid />}
              {pageKey === 'patient-center'  && <PatientCenterGrid />}
              {pageKey === 'contact'         && <ContactGrid />}
            </div>

            {/* ── Sidebar ──────────────────────────────────── */}
            <div className={s.sidebar}>
              <SidebarInfo />
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
